import { styled } from '@mui/material';
import type { FC, ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import CustomChevronIcon from 'assets/CustomChevronIcon';

export type DropdownItemId = string | number;

export interface DropdownItem {
  id: DropdownItemId;
  content: ReactNode;
}

export interface DarkThemeDropdownProps {
  items: DropdownItem[];
  selectedId: DropdownItemId;
  onChange: (id: DropdownItemId) => void;
  renderTrigger?: (selectedItem: DropdownItem, isOpen: boolean) => ReactNode;
  renderItem?: (item: DropdownItem, isSelected: boolean) => ReactNode;
}

export const DarkThemeDropdown: FC<DarkThemeDropdownProps> = memo(
  function DarkThemeDropdown({
    items,
    selectedId,
    onChange,
    renderTrigger,
    renderItem,
  }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const activeItem = items.find(
      (item) => String(item.id) === String(selectedId)
    );

    const handleItemClick = useCallback(
      (id: DropdownItemId) => {
        onChange(id);
        setOpen(false);
      },
      [onChange]
    );

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    if (items.length <= 1) {
      return (
        <Wrapper>
          <SingleItemContainer>
            {activeItem && renderTrigger
              ? renderTrigger(activeItem, false)
              : activeItem?.content}
          </SingleItemContainer>
        </Wrapper>
      );
    }

    return (
      <Wrapper ref={wrapperRef}>
        <TriggerButton
          type='button'
          $open={open}
          onClick={() => setOpen(!open)}
          data-testId='dropdown-trigger'
          style={{ visibility: open ? 'hidden' : 'visible' }}
        >
          {activeItem && renderTrigger
            ? renderTrigger(activeItem, open)
            : activeItem?.content}
          <ChevronIcon as={CustomChevronIcon} $open={open} />
        </TriggerButton>

        {open && (
          <Dropdown>
            <List data-testid='dropdown-list'>
              {items.map((item) => {
                const isSelected = String(item.id) === String(selectedId);
                return (
                  <ItemLi key={String(item.id)}>
                    <ItemButton
                      type='button'
                      $selected={isSelected}
                      onClick={() => handleItemClick(item.id)}
                      tabIndex={0}
                    >
                      {renderItem ? renderItem(item, isSelected) : item.content}
                    </ItemButton>
                  </ItemLi>
                );
              })}
            </List>
          </Dropdown>
        )}
      </Wrapper>
    );
  }
);

const COLORS = {
  primary: '#51DBCD',
  background: {
    closed: '#454443',
    open: '#222A33',
    dropdown: '#222A33',
  },
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.5)',
  },
  hover: 'rgba(255, 255, 255, 0.05)',
};

const DIMENSIONS = {
  padding: '12px 16px',
  minHeight: '48px',
  iconSize: '24px',
};

const Wrapper = styled('div')`
  position: relative;
`;

const TriggerButton = styled('button')<{ $open: boolean }>`
  border: none;
  margin: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  background-color: ${({ $open }) =>
    $open ? COLORS.background.open : COLORS.background.closed};
  border-radius: 0px;
  transition: background-color 150ms ease-in-out;
  position: relative;
  font-size: 16px;
  line-height: 20px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  padding: ${DIMENSIONS.padding};
  padding-right: 56px;
  min-height: ${DIMENSIONS.minHeight};
  display: flex;
  align-items: center;
`;

const Dropdown = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1300;
  background-color: ${COLORS.background.dropdown};
  border: none;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.48);
  border-radius: 8px;
  overflow: hidden;
`;

const List = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
`;

const ItemLi = styled('li')`
  padding: 0;
  margin: 0;
`;

const ItemButton = styled('button')<{ $selected: boolean }>`
  width: 100%;
  border: none;
  background: ${COLORS.background.dropdown};
  cursor: pointer;
  color: ${COLORS.text.primary};
  padding: ${DIMENSIONS.padding};
  min-height: ${DIMENSIONS.minHeight};
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 150ms ease-in-out;
  outline: none;
  text-align: left;
  border-radius: 0 !important;
  display: flex;
  align-items: center;

  &:hover,
  &:focus {
    background: ${COLORS.hover};
  }
`;

const ChevronIcon = styled(CustomChevronIcon)<{ $open: boolean }>`
  color: ${COLORS.text.primary};
  width: ${DIMENSIONS.iconSize};
  height: ${DIMENSIONS.iconSize};
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%)
    ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  opacity: ${({ $open }) => ($open ? 0 : 1)};
  pointer-events: none;
`;

const SingleItemContainer = styled('div')`
  min-height: ${DIMENSIONS.minHeight};
  font-size: 16px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: ${COLORS.text.primary};
  border-radius: 0;
  display: flex;
  align-items: flex-start;
`;
